import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import {
  AbstractScheduleService,
  CameraScheduleComponent,
  GenericScheduleComponent,
  ScheduleServices,
} from '@vectronic/remote-commands';
import { combineLatest, Subject } from 'rxjs';

@Component({
  selector: 'vectronic-formly-schedule-control',
  imports: [GenericScheduleComponent, CameraScheduleComponent],
  template: `
    @if (scheduleType() === 'CAMERA') {
      <rc-camera-schedule
        [hideCameraFormat]="true"
        [showReset]="false"
        [showRemoveSchedule]="false"
        [title]="scheduleTitles()?.['CAMERA'] ?? 'Camera Schedule'"
      ></rc-camera-schedule>
    } @else if (scheduleType()) {
      <rc-generic-schedule
        [scheduleType]="scheduleType()"
        [showReset]="false"
        [showScheduleRemove]="true"
        [title]="scheduleTitles()?.[scheduleType()] ?? 'Schedule'"
        [initializeSequenceByAddSchedule]="true"
      ></rc-generic-schedule>
    }
  `,
})
export class FormlyScheduleControlComponent implements ControlValueAccessor, OnInit {
  private readonly scheduleServices = inject(ScheduleServices);
  private readonly destroyRef = inject(DestroyRef);
  readonly ngControl = inject(NgControl, { self: true, optional: true });

  scheduleType = input<dsEnums.ScheduleType | null>(null);
  scheduleTitles = input<Record<dsEnums.ScheduleType, string> | undefined>();

  disabled = false;

  readonly stateChanges = new Subject<void>();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: dsDtos.Schedule | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (!this.scheduleType()) return;

    const service = this.scheduleServices[this.scheduleType() as keyof ScheduleServices] as
      | AbstractScheduleService
      | undefined;

    if (!service || !('rules$' in service)) return;

    combineLatest({
      rules: service.rules$,
      isFilledAndChanged: service.isFilledAndChanged$,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ rules, isFilledAndChanged }) => {
        if (!isFilledAndChanged) {
          this.onChange(null);
          this.onTouched();
          return;
        }
        const scheduleRules = Object.values(rules ?? {}).filter(Boolean);
        const value: dsDtos.Schedule = {
          scheduleType: this.scheduleType() as dsEnums.ScheduleType,
          userDefined: true,
          rules:
            this.scheduleType() !== 'CAMERA' ? (scheduleRules as dsDtos.ScheduleRule[]) : undefined,
          cameraScheduleRules:
            this.scheduleType() === 'CAMERA'
              ? (scheduleRules as dsDtos.CameraScheduleRule[])
              : undefined,
        };
        this.onChange(value);
        this.onTouched();
      });
  }

  writeValue(value: dsDtos.Schedule | null): void {
    if (value) {
      this.scheduleServices[this.scheduleType()].loadRules(
        this.scheduleType() === 'CAMERA' ? value.cameraScheduleRules : value.rules,
      );
    } else {
      this.scheduleServices[this.scheduleType()].onRemoveSchedule();
    }
  }

  registerOnChange(fn: (value: dsDtos.Schedule | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }
}
